import H from '@here/maps-api-for-javascript';
import { CompoundCadLayer } from '@/modules/commandCentre/compound/CompoundCadLayer';
import {
  CompoundMetadata,
  CompoundSlot,
  CompoundSlots,
  CompoundOccupancyStatus,
  SimulatedVehicle,
} from '@/types/CompoundMap';
import { featureId, isMapped, SlotSpatialIndex } from './spatial';

export const DRAWING_HEADING = 150.6730766907672;
type PointerEvent = {
  target?: { getData?: () => unknown };
  currentPointer?: { viewportX: number; viewportY: number };
};
type SlotStyle = CompoundOccupancyStatus | 'mapped' | 'unresolved';
type SlotBatch = { ids: string[]; polygon: H.map.Polygon };
type SlotChunk = { ids: string[]; batches: Map<SlotStyle, SlotBatch> };

/** Owns only candidate layers; never creates a map or changes personnel objects. */
export class CompoundMapAdapter {
  private readonly group = new H.map.Group({
    data: 'compound-slots',
    volatility: true,
    zIndex: -10,
  });
  private readonly highlightGroup = new H.map.Group({
    data: 'compound-highlights',
    volatility: true,
    zIndex: -9,
  });
  private readonly index: SlotSpatialIndex;
  private readonly geometries = new Map<string, H.geo.Polygon>();
  private readonly features: Map<string, CompoundSlot>;
  private readonly chunks: SlotChunk[] = [];
  private readonly slotChunks = new Map<string, SlotChunk>();
  private readonly renderedStates = new Map<string, SlotStyle>();
  private readonly highlights = new Map<string, H.map.Polygon>();
  private readonly listeners: Array<[string, (event: PointerEvent) => void]> =
    [];
  private disposed = false;
  private ready = false;
  private dragging = false;
  private lastDragEnd = -Infinity;
  private hoverFrame = 0;
  private selected: string | null = null;
  private hovered: string | null = null;
  private visible = true;
  private vehicles = new Map<string, SimulatedVehicle>();
  private recorded: ReadonlyMap<string, CompoundOccupancyStatus> | null = null;
  private cad?: CompoundCadLayer;
  private readonly started = performance.now();
  private preparedMs = 0;
  private batchBuilds = 0;
  private slotUpdateCount = 0;
  private slotUpdateSkipped = 0;
  private lastChangedSlotCount = 0;
  private lastChangedBatchCount = 0;
  private changedBatchCount = 0;
  private lastSlotUpdateMs = 0;
  private hoverEvents = 0;
  private selectionEvents = 0;
  private cameraEvents = 0;

  constructor(
    private readonly map: H.Map,
    private readonly metadata: CompoundMetadata,
    private readonly data: CompoundSlots,
    private readonly onSelect: (id: string | null) => void,
    private readonly onHover: (id: string | null) => void,
    private readonly onCadError: (error: Error) => void,
  ) {
    this.index = new SlotSpatialIndex(data.features);
    this.features = new Map(data.features.map((f) => [featureId(f), f]));
    const listen = (name: string, callback: (event: PointerEvent) => void) => {
      map.addEventListener(name, callback);
      this.listeners.push([name, callback]);
    };
    listen('dragstart', () => {
      this.dragging = true;
      this.clearHover();
    });
    listen('dragend', () => {
      this.dragging = false;
      this.lastDragEnd = performance.now();
    });
    listen('mapviewchange', () => {
      this.cameraEvents += 1;
      this.clearHover();
    });
    listen('pointerleave', () => this.clearHover());
    listen('pointermove', (event) => {
      cancelAnimationFrame(this.hoverFrame);
      this.hoverFrame = requestAnimationFrame(() => {
        if (this.disposed || this.dragging) return;
        const id = this.hit(event);
        if (id === this.hovered) return;
        this.hovered = id;
        this.hoverEvents += 1;
        this.refreshHighlights();
        this.onHover(id);
      });
    });
    listen('tap', (event) => {
      if (this.dragging || performance.now() - this.lastDragEnd < 250) return;
      const id = this.hit(event);
      if (id) this.select(id);
    });
  }

  async prepare() {
    for (let offset = 0; offset < this.data.features.length; offset += 400) {
      if (this.disposed) return;
      for (const feature of this.data.features.slice(offset, offset + 400)) {
        const rings = feature.geometry.coordinates.map((ring) => {
          const line = new H.geo.LineString();
          ring.forEach(([lng, lat]) => line.pushPoint({ lat, lng }));
          return line;
        });
        this.geometries.set(
          featureId(feature),
          new H.geo.Polygon(rings[0], rings.slice(1)),
        );
      }
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
    if (this.disposed) return;
    this.prepareBatches();
    this.map.addObject(this.group);
    this.map.addObject(this.highlightGroup);
    this.ready = true;
    this.preparedMs = performance.now() - this.started;
    this.fit();
  }

  private hit(event: PointerEvent) {
    if (!this.ready || !this.visible || !event.currentPointer) return null;
    // Personnel markers and HERE controls retain the event; do not select underneath.
    if (
      event.target &&
      event.target !== this.map &&
      event.target.getData?.() !== 'compound-slot'
    )
      return null;
    const point = this.map.screenToGeo(
      event.currentPointer.viewportX,
      event.currentPointer.viewportY,
    );
    if (!point) return null;
    const matches = this.index.at(point.lng, point.lat);
    if (matches.some((f) => featureId(f) === this.selected))
      return this.selected;
    const scale = Math.cos((point.lat * Math.PI) / 180);
    const distance = (f: CompoundSlot) =>
      ((f.properties.longitude - point.lng) * scale) ** 2 +
      (f.properties.latitude - point.lat) ** 2;
    matches.sort((a, b) => distance(a) - distance(b));
    return matches.length ? featureId(matches[0]) : null;
  }

  private style(feature: CompoundSlot, highlight = false) {
    if (this.recorded) {
      const status = this.recorded.get(featureId(feature)) ?? 'unknown';
      const colours: Record<CompoundOccupancyStatus, [string, string]> = {
        occupied: ['rgba(239,68,68,0.52)', '#db3545'],
        unrecorded: ['rgba(34,197,94,0.25)', '#22c55e'],
        unknown: ['rgba(148,163,184,0.35)', '#94a3b8'],
        conflict: ['rgba(245,158,11,0.55)', '#f59e0b'],
      };
      return {
        fillColor: colours[status][0],
        strokeColor: highlight ? '#ffd700' : colours[status][1],
        lineWidth: highlight ? 3 : 0.9,
      };
    }
    const occupied = this.vehicles.has(featureId(feature));
    return {
      fillColor: occupied
        ? 'rgba(239,68,68,0.52)'
        : isMapped(feature)
          ? 'rgba(118,199,235,0.30)'
          : 'rgba(183,148,223,0.33)',
      strokeColor: highlight
        ? '#ffd700'
        : occupied
          ? '#db3545'
          : isMapped(feature)
            ? '#76c7eb'
            : '#b794df',
      lineWidth: highlight ? 3 : 0.9,
    };
  }

  private visualState(feature: CompoundSlot): SlotStyle {
    if (this.recorded)
      return this.recorded.get(featureId(feature)) ?? 'unknown';
    if (this.vehicles.has(featureId(feature))) return 'occupied';
    return isMapped(feature) ? 'mapped' : 'unresolved';
  }

  private prepareBatches() {
    this.batchBuilds += 1;
    const cells = new Map<string, CompoundSlot[]>();
    for (const feature of this.data.features) {
      const { latitude, longitude } = feature.properties;
      const cell = `${Math.floor(latitude / 0.001)}:${Math.floor(longitude / 0.001)}`;
      if (!cells.has(cell)) cells.set(cell, []);
      cells.get(cell)!.push(feature);
      this.renderedStates.set(featureId(feature), this.visualState(feature));
    }
    for (const features of cells.values()) {
      features.sort(
        (a, b) =>
          a.properties.longitude - b.properties.longitude ||
          a.properties.latitude - b.properties.latitude ||
          featureId(a).localeCompare(featureId(b)),
      );
      for (let offset = 0; offset < features.length; offset += 256) {
        const chunk: SlotChunk = {
          ids: features.slice(offset, offset + 256).map(featureId),
          batches: new Map(),
        };
        this.chunks.push(chunk);
        chunk.ids.forEach((id) => this.slotChunks.set(id, chunk));
        this.updateChunk(chunk);
      }
    }
  }

  private updateChunk(chunk: SlotChunk) {
    const groups = new Map<SlotStyle, string[]>();
    for (const id of chunk.ids) {
      const state = this.renderedStates.get(id)!;
      if (!groups.has(state)) groups.set(state, []);
      groups.get(state)!.push(id);
    }
    let changed = 0;
    for (const [state, batch] of chunk.batches) {
      if (groups.has(state)) continue;
      this.group.removeObject(batch.polygon);
      chunk.batches.delete(state);
      changed += 1;
    }
    for (const [state, ids] of groups) {
      const batch = chunk.batches.get(state);
      if (
        batch &&
        batch.ids.length === ids.length &&
        batch.ids.every((id, index) => id === ids[index])
      )
        continue;
      const geometry = new H.geo.MultiPolygon(
        ids.map((id) => this.geometries.get(id)!),
      );
      if (batch) {
        batch.ids = ids;
        batch.polygon.setGeometry(geometry);
      } else {
        const polygon = new H.map.Polygon(geometry, {
          data: 'compound-slot',
          style: this.style(this.features.get(ids[0])!),
        });
        chunk.batches.set(state, { ids, polygon });
        this.group.addObject(polygon);
      }
      changed += 1;
    }
    return changed;
  }

  private updateSlotStates() {
    this.slotUpdateCount += 1;
    this.lastChangedSlotCount = 0;
    this.lastChangedBatchCount = 0;
    this.lastSlotUpdateMs = 0;
    if (!this.ready) return;
    const started = performance.now();
    const changed = new Set<string>();
    const chunks = new Set<SlotChunk>();
    for (const feature of this.data.features) {
      const id = featureId(feature);
      const state = this.visualState(feature);
      if (state === this.renderedStates.get(id)) continue;
      changed.add(id);
      chunks.add(this.slotChunks.get(id)!);
      this.renderedStates.set(id, state);
    }
    if (!changed.size) this.slotUpdateSkipped += 1;
    for (const chunk of chunks)
      this.lastChangedBatchCount += this.updateChunk(chunk);
    this.lastChangedSlotCount = changed.size;
    this.changedBatchCount += this.lastChangedBatchCount;
    if (
      (this.selected && changed.has(this.selected)) ||
      (this.hovered && changed.has(this.hovered))
    )
      this.refreshHighlights();
    this.lastSlotUpdateMs = performance.now() - started;
  }

  private refreshHighlights() {
    for (const [kind, id] of [
      ['selected', this.selected],
      ['hovered', this.hovered],
    ] as const) {
      let polygon = this.highlights.get(kind);
      if (
        !id ||
        !this.visible ||
        (kind === 'hovered' && id === this.selected)
      ) {
        polygon?.setVisibility(false);
        continue;
      }
      const geometry = this.geometries.get(id);
      if (!geometry) continue;
      if (!polygon) {
        polygon = new H.map.Polygon(geometry, {
          data: 'compound-slot',
          style: this.style(this.features.get(id)!, true),
        });
        this.highlights.set(kind, polygon);
        this.highlightGroup.addObject(polygon);
      } else {
        if (polygon.getGeometry() !== geometry) polygon.setGeometry(geometry);
        polygon.setStyle(this.style(this.features.get(id)!, true));
        polygon.setVisibility(true);
      }
    }
  }

  private clearHover() {
    cancelAnimationFrame(this.hoverFrame);
    if (this.hovered) {
      this.hovered = null;
      this.onHover(null);
      this.refreshHighlights();
    }
  }

  select(id: string | null, focus = false) {
    this.selectionEvents += 1;
    this.selected = id;
    this.refreshHighlights();
    this.onSelect(id);
    const feature = id ? this.features.get(id) : undefined;
    if (focus && feature)
      this.map.getViewModel().setLookAtData(
        {
          position: {
            lat: feature.properties.latitude,
            lng: feature.properties.longitude,
          },
          zoom: 21,
          tilt: 0,
          heading: DRAWING_HEADING,
        },
        false,
      );
  }
  setSimulation(vehicles: Map<string, SimulatedVehicle>) {
    if (this.disposed) return;
    this.recorded = null;
    this.vehicles = new Map(vehicles);
    this.updateSlotStates();
  }
  setRecordedOccupancy(statuses: ReadonlyMap<string, CompoundOccupancyStatus>) {
    if (this.disposed) return;
    this.recorded = new Map(statuses);
    this.vehicles = new Map();
    this.updateSlotStates();
  }
  setSlotsVisible(visible: boolean) {
    this.visible = visible;
    this.group.setVisibility(visible);
    this.clearHover();
    this.refreshHighlights();
  }
  zoom(delta: number) {
    this.map.setZoom(
      Math.min(22, Math.max(13, this.map.getZoom() + delta)),
      false,
    );
  }
  fit() {
    const b = this.metadata.bounds!;
    const latPad = (b.north - b.south) * 0.08,
      lngPad = (b.east - b.west) * 0.08;
    this.map.getViewModel().setLookAtData(
      {
        bounds: new H.geo.Rect(
          b.north + latPad,
          b.west - lngPad,
          b.south - latPad,
          b.east + lngPad,
        ),
        tilt: 0,
        heading: DRAWING_HEADING,
      },
      false,
    );
  }

  setCad(visible: boolean, opacity: number, linework: boolean) {
    this.removeCad();
    if (!visible || this.disposed) return;
    this.cad = new CompoundCadLayer(
      this.map,
      this.metadata,
      linework,
      opacity,
      this.onCadError,
    );
  }
  setCadOpacity(opacity: number) {
    this.cad?.setOpacity(opacity);
  }
  diagnostics() {
    const view = this.map.getViewModel().getLookAtData();
    return {
      ready: this.ready,
      disposed: this.disposed,
      candidateCount: this.features.size,
      nativeBatchCount: this.group.getObjects().length,
      highlightCount: this.highlights.size,
      batchBuilds: this.batchBuilds,
      slotChunkCount: this.chunks.length,
      slotUpdateCount: this.slotUpdateCount,
      slotUpdateSkipped: this.slotUpdateSkipped,
      lastChangedSlotCount: this.lastChangedSlotCount,
      lastChangedBatchCount: this.lastChangedBatchCount,
      changedBatchCount: this.changedBatchCount,
      lastSlotUpdateMs: this.lastSlotUpdateMs,
      hoverEvents: this.hoverEvents,
      selectionEvents: this.selectionEvents,
      cameraEvents: this.cameraEvents,
      preparationMs: Math.round(this.preparedMs),
      volatileSlots: this.group.getVolatility(),
      volatileHighlights: this.highlightGroup.getVolatility(),
      zoom: this.map.getZoom(),
      heading: view.heading,
      cadAttached: Boolean(this.cad?.diagnostics().ready),
      cad: this.cad?.diagnostics() ?? null,
      simulatedCount: this.vehicles.size,
      readonly: true,
      timingScope: 'sampled-local-preparation-not-performance-acceptance',
    };
  }
  private removeCad() {
    this.cad?.dispose();
    this.cad = undefined;
  }
  dispose() {
    this.disposed = true;
    cancelAnimationFrame(this.hoverFrame);
    this.listeners.forEach(([name, callback]) =>
      this.map.removeEventListener(name, callback),
    );
    this.removeCad();
    if (this.ready) {
      this.map.removeObject(this.group);
      this.map.removeObject(this.highlightGroup);
    }
    this.group.dispose();
    this.highlightGroup.dispose();
    this.geometries.clear();
    this.features.clear();
    this.chunks.length = 0;
    this.slotChunks.clear();
    this.renderedStates.clear();
  }
}
